import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Youtube, Instagram, Tv, Radio, ExternalLink, RefreshCcw } from "lucide-react";
import { useCountUp, formatCompactIN, formatFullIN, relativeTime } from "../lib/useCountUp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_EVERY = 60 * 1000; // 1-min UI poll; server refreshes YT every 15 min

/* ── Animated metric value ─────────────────────────────────────────── */
const Metric = ({ value, label, compact = false, decimals = 0, enabled }) => {
  const animated = useCountUp(value || 0, { enabled, duration: 1600 });
  const display = compact
    ? formatCompactIN(animated)
    : decimals
      ? animated.toFixed(decimals)
      : formatFullIN(animated);
  return (
    <div>
      <div className="st-cormorant text-3xl md:text-4xl font-bold text-[var(--cream)] leading-tight tabular-nums">
        {display}
      </div>
      <div className="st-cinzel text-[0.6rem] md:text-[0.7rem] font-bold tracking-[0.28em] uppercase text-[var(--fog)] mt-1">
        {label}
      </div>
    </div>
  );
};

/* ── Glass card shell ──────────────────────────────────────────────── */
const Card = ({ icon: Icon, accent, title, subtitle, status, statusColor = "#9ED4F2", children, footer, dataTestid }) => (
  <div
    data-testid={dataTestid}
    className="relative p-6 md:p-7 backdrop-blur-md"
    style={{
      background: "linear-gradient(160deg, rgba(20,16,10,.82) 0%, rgba(8,6,4,.92) 100%)",
      border: "1px solid rgba(201,160,74,.18)",
      boxShadow: "0 30px 60px rgba(0,0,0,.45), 0 0 0 1px rgba(201,160,74,.06) inset",
    }}
  >
    {/* gold corner accents */}
    <span className="absolute top-0 left-0 w-5 h-5 border-t border-l border-[var(--gold)] opacity-70" />
    <span className="absolute top-0 right-0 w-5 h-5 border-t border-r border-[var(--gold)] opacity-70" />
    <span className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-[var(--gold)] opacity-70" />
    <span className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-[var(--gold)] opacity-70" />

    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 inline-flex items-center justify-center"
          style={{ background: accent || "rgba(201,160,74,.12)", color: "#fff" }}
        >
          {Icon ? <Icon size={18} /> : null}
        </div>
        <div>
          <div className="st-cormorant text-xl md:text-2xl font-bold text-[var(--cream)] leading-tight">{title}</div>
          {subtitle && (
            <div className="st-cinzel text-[0.6rem] md:text-xs font-semibold tracking-[0.28em] uppercase text-[var(--gold-dk)] mt-0.5">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {status && (
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1"
          style={{ background: `${statusColor}1a`, border: `1px solid ${statusColor}55` }}
        >
          <span className="block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: statusColor }} />
          <span className="st-cinzel text-[0.55rem] md:text-[0.65rem] font-bold tracking-[0.28em] uppercase" style={{ color: statusColor }}>
            {status}
          </span>
        </div>
      )}
    </div>

    <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">{children}</div>

    {footer && (
      <div className="mt-6 pt-4 border-t border-[rgba(201,160,74,.12)] st-infant text-[0.78rem] text-[var(--fog)]">
        {footer}
      </div>
    )}
  </div>
);

/* ── Public Impact Dashboard ───────────────────────────────────────── */
export const ImpactDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const sectionRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/impact`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.message || "Failed to load impact metrics");
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_EVERY);
    return () => clearInterval(id);
  }, []);

  // Start counter animations only when the section is in view
  useEffect(() => {
    if (!sectionRef.current || typeof IntersectionObserver === "undefined") {
      setEnabled(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setEnabled(true); },
      { threshold: 0.15 }
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  const yt = data?.youtube;
  const m = data?.manual || {};

  return (
    <section
      ref={sectionRef}
      data-testid="impact-dashboard"
      className="relative w-screen py-20 md:py-28 px-5 md:px-16 overflow-hidden"
      style={{ background: "var(--coal)" }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 65% 50% at 50% 0%, rgba(201,160,74,.07) 0%, transparent 65%)",
        }}
      />
      <div className="relative z-[2] max-w-6xl mx-auto">
        <div className="text-center">
          <span className="st-eyebrow">Live · Soul Trip Impact</span>
          <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
            The numbers behind the journey
          </h2>
          <div className="st-rule mx-auto mt-5"/>
          <p className="mt-5 st-cormorant italic text-base md:text-lg text-[var(--fog)] max-w-2xl mx-auto leading-relaxed">
            Real-time performance across every platform we tell stories on. Refreshes automatically.
          </p>
        </div>

        {error && (
          <div data-testid="impact-error" className="mt-10 mx-auto max-w-xl p-4 text-center st-infant text-sm text-[#ff9a9a]"
               style={{ border: "1px solid rgba(255,80,80,.45)", background: "rgba(255,80,80,.06)" }}>
            {error}
          </div>
        )}

        <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-7">
          {/* ── YouTube ── */}
          <Card
            dataTestid="impact-card-youtube"
            icon={Youtube}
            accent="#e21e26"
            title="YouTube"
            subtitle={yt?.channel_handle ? `@${yt.channel_handle}` : "@soultrip-talks"}
            status={yt ? "Live" : "Loading"}
            statusColor="#7CE495"
            footer={
              yt?.latest_video ? (
                <a
                  href={`https://youtu.be/${yt.latest_video.video_id}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="impact-yt-latest"
                  className="inline-flex items-center gap-2 no-underline text-[var(--gold-lt)] hover:text-[var(--gold)] transition-colors"
                >
                  <span className="st-cinzel text-[0.6rem] font-bold tracking-[0.28em] uppercase text-[var(--gold)]">Latest Drop</span>
                  <span className="st-cormorant text-base font-semibold truncate max-w-[280px] md:max-w-[420px]">
                    {yt.latest_video.title}
                  </span>
                  <ExternalLink size={12}/>
                </a>
              ) : "Updating shortly…"
            }
          >
            <Metric enabled={enabled} value={yt?.subscriber_count} label="Subscribers" compact />
            <Metric enabled={enabled} value={yt?.view_count} label="Lifetime Views" compact />
            <Metric enabled={enabled} value={yt?.video_count} label="Videos" />
          </Card>

          {/* ── Instagram ── */}
          <Card
            dataTestid="impact-card-instagram"
            icon={Instagram}
            accent="linear-gradient(135deg,#feda75,#fa7e1e 25%,#d62976 55%,#962fbf 80%,#4f5bd5)"
            title="Instagram"
            subtitle="@gandabherundaarts"
            status="Admin · Updated"
            statusColor="#E48EC0"
          >
            <Metric enabled={enabled} value={m.instagram_followers} label="Followers" compact />
            <Metric enabled={enabled} value={m.instagram_reels_views} label="Reels Views" compact />
            <Metric enabled={enabled} value={m.instagram_accounts_reached} label="Accounts Reached" compact />
            <Metric enabled={enabled} value={m.instagram_profile_visits} label="Profile Visits" compact />
            <Metric enabled={enabled} value={m.instagram_engagement_rate} label="Engagement %" decimals={1} />
          </Card>

          {/* ── ETV Win ── */}
          <Card
            dataTestid="impact-card-etvwin"
            icon={Tv}
            accent="#e21e26"
            title="ETV Win"
            subtitle="Streaming · Soul Trip Series"
            status="OTT · Synced"
            statusColor="#FF9595"
            footer={
              <span>
                <span className="st-cinzel text-[0.6rem] font-bold tracking-[0.28em] uppercase text-[var(--gold)]">Top Episode</span>
                {" "}<span className="st-cormorant text-base text-[var(--cream)]">{m.etvwin_top_episode_label || "—"}</span>
                {m.etvwin_top_episode_views ? (
                  <span className="ml-2 st-cormorant text-base text-[var(--gold-lt)]">· {formatCompactIN(m.etvwin_top_episode_views)} views</span>
                ) : null}
              </span>
            }
          >
            <Metric enabled={enabled} value={m.etvwin_ott_views} label="OTT Views" compact />
            <Metric enabled={enabled} value={m.etvwin_ott_streaming_minutes} label="Streaming Mins" compact />
            <Metric enabled={enabled} value={m.etvwin_total_watch_hours} label="Watch Hours" compact />
          </Card>

          {/* ── TV9 Telugu ── */}
          <Card
            dataTestid="impact-card-tv9"
            icon={Radio}
            accent="linear-gradient(135deg,#0033a0,#e21e26)"
            title="TV9 Telugu"
            subtitle="Broadcasting Partner"
            status={m.tv9_streaming_status || "Streaming Soon"}
            statusColor="#9ED4F2"
            footer="Catch Soul Trip across TV9 Telugu's primetime lineup — schedule reveal coming soon."
          >
            <Metric enabled={enabled} value={m.sponsor_count} label="Sponsors Onboard" />
            <Metric enabled={enabled} value={m.media_mentions} label="Media Mentions" />
          </Card>
        </div>

        {/* Footer meta */}
        <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 st-cinzel text-[0.6rem] md:text-xs font-bold tracking-[0.28em] uppercase text-[var(--gold-dk)]">
          <span className="inline-flex items-center gap-2">
            <RefreshCcw size={11} className="text-[var(--gold)]"/>
            YouTube refreshes every 15 min
          </span>
          {data?.youtube?.fetched_at && (
            <span data-testid="impact-yt-updated">
              YT updated · {relativeTime(data.youtube.fetched_at)}
            </span>
          )}
          {data?.manual?.updated_at && (
            <span data-testid="impact-manual-updated">
              Manual metrics · {relativeTime(data.manual.updated_at)}
            </span>
          )}
        </div>
      </div>
    </section>
  );
};
